import React from 'react';
import { Loader, Modal } from '@mantine/core';
import { KITCHEN_STATUS_LABELS } from '../monitoring-page.constants';
import { formatDateTime, kdsStatusClassName } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type KdsAuditHistoryModalProps = Pick<
  UseMonitoringPage,
  'kdsHistoryTarget' | 'kdsHistory' | 'isKdsHistoryLoading' | 'closeKdsHistoryModal'
>;

const statusLabel = (status: 'COOKING' | 'READY' | 'DONE' | 'HIDE' | null) =>
  status ? KITCHEN_STATUS_LABELS[status] : 'נשלח למטבח';

export const KdsAuditHistoryModal: React.FC<KdsAuditHistoryModalProps> = ({
  kdsHistoryTarget,
  kdsHistory,
  isKdsHistoryLoading,
  closeKdsHistoryModal,
}) => {
  const target = kdsHistoryTarget;
  const sortedTransitions = [...(kdsHistory?.transitions ?? [])].sort((a, b) => {
    const byTime = new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime();
    if (byTime !== 0) {
      return byTime;
    }
    return b.id - a.id;
  });
  const activeTransitionId = sortedTransitions[0]?.id ?? null;
  const contextLine = target
    ? `הזמנה ${target.orderCode}${target.tableName ? ` · שולחן ${target.tableName}` : ''}`
    : '';

  return (
    <Modal
      opened={target != null}
      onClose={closeKdsHistoryModal}
      title={
        target ? (
          <div className={classes.historyTitle}>
            <span className={classes.historyProduct}>{target.productName}</span>
            <span className={classes.historyContext}>{contextLine}</span>
          </div>
        ) : ''
      }
      centered
      size="lg"
      closeButtonProps={{ 'data-testid': 'monitoring-kds-history-close' } as Record<string, string>}
    >
      {isKdsHistoryLoading ? (
        <div className={classes.loadingWrap}>
          <Loader />
        </div>
      ) : (
        <div className={classes.auditList}>
          {sortedTransitions.map((row, index) => {
            const isQuantityChanged = row.eventType === 'quantity_changed';
            const isCancelledEvent = isQuantityChanged && row.quantity === 0;
            const previousQuantity = sortedTransitions[index + 1]?.quantity;
            return (
              <div key={row.id} className={classes.auditRow}>
                <div className={classes.auditHeader}>
                  {isCancelledEvent ? (
                    <span className={`${classes.kdsStatus} ${classes.kdsStatusCancelled}`}>{'בוטל'}</span>
                  ) : isQuantityChanged ? (
                    <span className={`${classes.kdsStatus} ${classes.kdsStatusMuted}`}>
                      {`כמות שונתה מ־${previousQuantity ?? '?'} ל־${row.quantity ?? '?'}`}
                    </span>
                  ) : (
                    <span className={kdsStatusClassName(classes, row.toKitchenStatus)}>
                      {statusLabel(row.toKitchenStatus)}{row.id === activeTransitionId ? ' · פעיל כעת' : ''}
                    </span>
                  )}
                  <span>{formatDateTime(row.occurredAt)}</span>
                </div>
                <div className={classes.auditMeta}>
                  {row.actorName ?? 'מערכת'}
                  {!isQuantityChanged && row.quantity != null ? ` · כמות: ${row.quantity}` : ''}
                </div>
              </div>
            );
          })}
          {sortedTransitions.length === 0 && (
            <div className={classes.empty}>{'אין היסטוריה עבור שורה זו.'}</div>
          )}
        </div>
      )}
    </Modal>
  );
};
